import { Modal, Button, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createMainSalesPlanStart } from "../../../Store/Slices/salesPlanSlice";
import { useState, useEffect } from "react";

const currentYear = new Date().getFullYear();
const pastYears = 2;
const futureYears = 1;

function getMonthName(month) {
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return monthNames[month - 1];
}

const getOptions = (type, salesPlanPeriodType, currentPlanPeriod) => {
  const groupedOptions = [];
  const quarterMonths = {
    "Jan - Dec": {
      1: ["JAN", "FEB", "MAR"],
      2: ["APR", "MAY", "JUN"],
      3: ["JUL", "AUG", "SEP"],
      4: ["OCT", "NOV", "DEC"],
    },
    "Apr - Mar": {
      1: ["APR", "MAY", "JUN"],
      2: ["JUL", "AUG", "SEP"],
      3: ["OCT", "NOV", "DEC"],
      4: ["JAN", "FEB", "MAR"],
    },
  };
  const startMonth = salesPlanPeriodType === "Jan - Dec" ? 1 : 4;

  for (let i = -pastYears; i <= futureYears; i++) {
    const year = currentYear + i;
    const label = i < 0 ? "Completed" : i === 0 ? "Current" : "Future";
    const options = [];

    if (type === "quarterly") {
      for (let j = 1; j <= 4; j++) {
        const months = quarterMonths[salesPlanPeriodType][j];
        const optionLabel = `Q${j} (${months.join(" - ")}) ${year}`;
        if (optionLabel !== currentPlanPeriod) {
          options.push({ label: optionLabel, value: optionLabel });
        }
      }
    } else {
      for (let j = startMonth; j <= 12; j++) {
        const optionLabel = `${getMonthName(j)} ${year}`;
        if (optionLabel !== currentPlanPeriod) {
          options.push({ label: optionLabel, value: optionLabel });
        }
      }
      for (let j = 1; j < startMonth; j++) {
        const optionLabel = `${getMonthName(j)} ${year + 1}`;
        if (optionLabel !== currentPlanPeriod) {
          options.push({ label: optionLabel, value: optionLabel });
        }
      }
    }
    if (options.length > 0) {
      groupedOptions.push({ label, options });
    }
  }
  return groupedOptions;
};

export default function CreateSalesPlanModal({ show, onHide }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const salesPlan = useSelector((state) => state.salesPlan.data);
  const isLoading = useSelector((state) => state.salesPlan.isLoading || false);
  const createdSalesPlanId = useSelector(
    (state) => state.salesPlan.createdSalesPlanId || null
  );
  const error = useSelector((state) => state.salesPlan.error || null);

  const [formData, setFormData] = useState({
    sales_plan_name: "",
    sales_plan_period: "",
    sales_plan_target: "",
    sales_plan_option: "new",
    sales_plan_type: "revenue_based",
    sales_plan_period_type: "Jan - Dec",
    sales_plan_calculation: "monthly",
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!show) {
      setFormData({
        sales_plan_name: "",
        sales_plan_period: "",
        sales_plan_target: "",
        sales_plan_option: "new",
        sales_plan_type: "revenue_based",
        sales_plan_period_type: "Jan - Dec",
        sales_plan_calculation: "monthly",
      });
      setErrors({});
      setSubmitted(false);
    }
  }, [show]);

  useEffect(() => {
    if (submitted && !isLoading) {
      const salesPlanId =
        createdSalesPlanId ||
        (typeof salesPlan === "object" && salesPlan?._id
          ? salesPlan._id
          : null);
      if (salesPlanId) {
        onHide();
        navigate("/digylax/form-module/target-tree", {
          state: {
            salesPlanId,
            isNewPlan: true,
            salesPlanData: { ...formData, main_sales_plan_id: salesPlanId },
          },
        });
        setSubmitted(false);
      } else if (error) {
        setErrors((prev) => ({
          ...prev,
          apiError: error || "Failed to create sales plan",
        }));
        setSubmitted(false);
      }
    }
  }, [
    createdSalesPlanId,
    isLoading,
    submitted,
    error,
    navigate,
    onHide,
    formData,
    salesPlan,
  ]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.sales_plan_name.trim()) {
      newErrors.sales_plan_name = "Sales plan name is required";
    }
    if (!formData.sales_plan_period) {
      newErrors.sales_plan_period = "Sales plan period is required";
    }
    if (
      !formData.sales_plan_target ||
      isNaN(formData.sales_plan_target) ||
      formData.sales_plan_target <= 0
    ) {
      newErrors.sales_plan_target = "Valid sales plan target is required";
    }
    return newErrors;
  };

  const handleNext = (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const payload = {
      sales_plan_name: formData.sales_plan_name,
      sales_plan_period: formData.sales_plan_period,
      sales_plan_target: parseFloat(formData.sales_plan_target),
      sales_plan_option: formData.sales_plan_option,
      sales_plan_type: formData.sales_plan_type,
      sales_plan_period_type: formData.sales_plan_period_type,
      sales_plan_calculation: formData.sales_plan_calculation,
    };

    dispatch(createMainSalesPlanStart(payload));
    setSubmitted(true);
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Create Sales Plan</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleNext}>
          {errors.apiError && (
            <div className="alert alert-danger">{errors.apiError}</div>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Sales Plan Name</Form.Label>
            <Form.Control
              type="text"
              name="sales_plan_name"
              value={formData.sales_plan_name}
              onChange={handleChange}
              isInvalid={!!errors.sales_plan_name}
            />
            <Form.Control.Feedback type="invalid">
              {errors.sales_plan_name}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Sales Plan Type</Form.Label>
            <Form.Select
              name="sales_plan_type"
              value={formData.sales_plan_type}
              onChange={handleChange}
            >
              <option value="revenue_based">Revenue Based</option>
              <option value="unit_based">Unit Based</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Sales Target</Form.Label>
            <Form.Control
              type="text"
              name="sales_plan_target"
              value={formData.sales_plan_target}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9.]/g, "");
                handleChange({ target: { name: "sales_plan_target", value } });
              }}
              isInvalid={!!errors.sales_plan_target}
            />
            <Form.Control.Feedback type="invalid">
              {errors.sales_plan_target}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>Sales Plan Period</Form.Label>
            <div className="d-flex">
              <Form.Check
                inline
                type="radio"
                label="Monthly"
                name="sales_plan_calculation"
                value="monthly"
                checked={formData.sales_plan_calculation === "monthly"}
                onChange={handleChange}
              />
              <Form.Check
                inline
                type="radio"
                label="Quarterly"
                name="sales_plan_calculation"
                value="quarterly"
                checked={formData.sales_plan_calculation === "quarterly"}
                onChange={handleChange}
              />
            </div>
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>Period Type</Form.Label>
            <div className="d-flex">
              <Form.Check
                inline
                type="radio"
                label="Jan - Dec"
                name="sales_plan_period_type"
                value="Jan - Dec"
                checked={formData.sales_plan_period_type === "Jan - Dec"}
                onChange={handleChange}
              />
              <Form.Check
                inline
                type="radio"
                label="Apr - Mar"
                name="sales_plan_period_type"
                value="Apr - Mar"
                checked={formData.sales_plan_period_type === "Apr - Mar"}
                onChange={handleChange}
              />
            </div>
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>Select Period</Form.Label>
            <Form.Select
              name="sales_plan_period"
              value={formData.sales_plan_period}
              onChange={handleChange}
              isInvalid={!!errors.sales_plan_period}
            >
              <option value="">Select Period</option>
              {getOptions(
                formData.sales_plan_calculation,
                formData.sales_plan_period_type,
                ""
              ).map((group, idx) => (
                <optgroup key={idx} label={group.label}>
                  {group.options.map((opt, subIdx) => (
                    <option key={subIdx} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.sales_plan_period}
            </Form.Control.Feedback>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleNext} disabled={isLoading}>
          Next
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
